import * as Sentry from '@sentry/react';
import Amplitude from 'amplitude-js';

import { Organization, ProductFeatureStatus } from 'dashboard/models/organization';
import { OrganizationPlan } from 'dashboard/models/organization-plan';
import { Role } from 'dashboard/models/role';
import { User } from 'dashboard/models/user';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { ApiModel } from 'lib/interfaces/api';

export function setIdOnReporters(
  user: User,
  impersonationSession: string | undefined,
  roles: Role[],
  organization: Organization,
  currentOrganizationPlan: OrganizationPlan | null,
  features: Record<string, boolean>,
) {
  if (import.meta.env.PROD) {
    Sentry.setUser({
      organizationId: organization.id,
      id: user.id,
      email: user.attributes?.['email-address'],
      username: [user.attributes?.['name-first'], user.attributes?.['name-last']]
        .filter(Boolean)
        .join(' '),
      impersonationSession,
    });
    Sentry.setTag('organization_slug', organization.attributes.slug);
    Sentry.setTag('customer_tier', organization.attributes['customer-tier']);
    Sentry.setTag('release', import.meta.env.VITE_COMMIT_SHA);
    Amplitude.getInstance().setUserId(user.id);
    Amplitude.getInstance().setGroup('organization-id', organization.id);

    Object.keys(features).forEach((featureKey) => {
      Amplitude.getInstance().setGroup(featureKey, features[featureKey].toString());
    });
  }

  if (['production', 'staging'].includes(import.meta.env.MODE) && !window.Cypress) {
    window.pendo.initialize({
      visitor: {
        id: user.id,
        status: user.attributes.status,
        name_first: user.attributes['name-first'],
        name_last: user.attributes['name-last'],
        email: user.attributes['email-address'],
        role_names: roles.map((role) => role.attributes.name),
        impersonation_session: impersonationSession,
        feature_flags: Object.entries(features)
          .filter(([_key, value]) => value)
          .map(([key, _value]) => key),
      },

      account: {
        id: organization.id,
        name: organization.attributes.slug,
        status: organization.attributes.status,
        customer_tier: organization.attributes['customer-tier'],
        current_organization_plan_key: currentOrganizationPlan?.attributes?.['key'],
        industry: organization.attributes.industry,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        product_features_production: organization.attributes['product-features']
          .filter(
            (feature) =>
              feature.status === ProductFeatureStatus.Enabled &&
              feature['environment-type'] === ApiModel.EnvironmentProduction,
          )
          .map((feature) => feature.name),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        product_features_sandbox: organization.attributes['product-features']
          .filter(
            (feature) =>
              feature.status === ProductFeatureStatus.Enabled &&
              feature['environment-type'] === ApiModel.EnvironmentSandbox,
          )
          .map((feature) => feature.name),
      },
    });
  }

  SegmentClient.sendSegmentIdentifyEvent(user.id, {
    firstname: user.attributes['name-first'],
    lastname: user.attributes['name-last'],
    email: user.attributes['email-address'],
    organizationid: organization.id,
    organizationname: organization.attributes.name,
    organizationdomain: organization.attributes.subdomain,
    customertier: organization.attributes['customer-tier'],
    role: roles[0],
    impersonationmode: impersonationSession,
    featureflags: Object.entries(features)
      .filter(([_key, value]) => value)
      .map(([key, _value]) => key),
  });

  const environmentSandbox = organization.relationships.environments.data.find(
    (env) => env.type === ApiModel.EnvironmentSandbox,
  );
  const environmentProduction = organization.relationships.environments.data.find(
    (env) => env.type === ApiModel.EnvironmentProduction,
  );

  SegmentClient.sendSegmentGroupEvent(organization.id, {
    companyname: organization.attributes.name,
    companydomain: organization.attributes.subdomain,
    sandboxid: environmentSandbox?.id,
    productionid: environmentProduction?.id,
    currentorganizationplankey: currentOrganizationPlan?.attributes?.['key'],
    customertier: organization.attributes['customer-tier'],
  });
}
